/**
 * PersonalFitPopover — hover/click "?" popover on the Personal-fit
 * score chip. Explains why the user scored the number they did:
 * priorities (weight) × the uni's per-axis score, with per-axis
 * contribution points and a note on skipped/missing axes.
 *
 * Extracted from pages/AdvancedResults.jsx (2026-02-04) to reduce
 * that file's bloat and to make the breakdown reusable on the
 * Shortlist page if we ever want it there.
 */
import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { HelpCircle } from "lucide-react";
import { track, EVENTS } from "@/lib/track";

export default function PersonalFitPopover({ breakdown, score, testid, onAdjustWeights }) {
  const [open, setOpen] = useState(false);
  const items = Array.isArray(breakdown) ? breakdown : [];
  const available = items.filter((b) => b.available);
  const missing = items.filter((b) => !b.available);
  const totalContribution = available.reduce((s, b) => s + (b.contribution_pts || 0), 0);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid || "personal-fit-why-btn"}
          aria-label="Why did I score this on Personal Fit?"
          className="inline-flex items-center justify-center text-stone-400 hover:text-[#4C1D95] transition-colors"
        >
          <HelpCircle className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        className="w-[340px] max-w-[92vw] p-0 border-stone-200 shadow-lg"
        data-testid="personal-fit-breakdown"
      >
        <div className="px-4 pt-4 pb-3 border-b border-stone-100 bg-stone-50 rounded-t-md">
          <div className="label-uppercase !text-[10px] text-[#4C1D95]">
            {available.length === 0
              ? "About your Personal fit score"
              : `Why ${score}% on Personal fit?`}
          </div>
          <p className="mt-1 text-[11px] text-stone-600 leading-snug">
            {available.length === 0
              ? `This university hasn't published anything yet on the axes ` +
                `you weigh — Prestige, Affordability, Career, Campus, City, ` +
                `Sport, Diversity. Rather than penalise it with a zero, we ` +
                `use a neutral baseline of ${score}/100 so it stays comparable ` +
                `with unis that have published data.`
              : "Your priorities (weight) × this university's score on each axis. " +
                "Contributions below sum to your personal score."}
          </p>
        </div>
        <div className="px-4 py-3 space-y-3 max-h-[60vh] overflow-y-auto">
          {available.length === 0 && (
            <div className="text-xs text-stone-600 leading-snug space-y-2" data-testid="personal-fit-empty">
              <p>
                <strong>What we'd normally show here:</strong> a
                per-axis bar chart of how well the uni scores on
                Prestige, Affordability, Career, Campus, City, Sport
                and Diversity — weighted by how much you care about
                each on the profile page.
              </p>
              <p className="text-stone-500">
                As we add editorial data for smaller universities you'll
                see this fill out. For now, the {score}/100 is a
                neutral floor — neither a bonus nor a penalty.
              </p>
            </div>
          )}
          {available.map((b) => (
            <div key={b.axis} data-testid={`personal-fit-axis-${b.axis}`}>
              <div className="flex items-baseline justify-between gap-2">
                <div className="text-[12px] font-semibold text-stone-800">{b.label}</div>
                <div className="font-mono text-[11px] text-stone-500 shrink-0">
                  {b.score_10?.toFixed(1)}/10 · you weigh it {b.user_weight}/10
                </div>
              </div>
              <div className="mt-1 flex items-center gap-2">
                <div
                  className="h-1.5 rounded-full bg-stone-100 relative overflow-hidden"
                  style={{ width: `${Math.max(6, b.weight_share_pct)}%` }}
                  title={`You give this axis ${b.weight_share_pct}% of your total priority weight`}
                >
                  <div
                    className="absolute inset-y-0 left-0 bg-[#4C1D95]/80 rounded-full"
                    style={{ width: `${((b.score_10 || 0) / 10) * 100}%` }}
                  />
                </div>
                <div className="font-mono text-[11px] text-[#4C1D95] shrink-0">
                  +{b.contribution_pts} pts
                </div>
              </div>
              <div className="mt-1 text-[10.5px] text-stone-500 leading-snug italic">
                {b.why}
              </div>
            </div>
          ))}

          {missing.length > 0 && (
            <div className="pt-2 border-t border-stone-100">
              <div className="label-uppercase !text-[9px] text-stone-400">Not published for this uni</div>
              <div className="mt-1 text-[10.5px] text-stone-500 leading-snug">
                {missing.map((m) => m.label).join(" · ")}
              </div>
              <div className="mt-0.5 text-[10px] text-stone-400 italic">
                These axes are skipped rather than counted as 0 so the score isn't unfairly punished.
              </div>
            </div>
          )}
        </div>
        <div className="px-4 py-2.5 border-t border-stone-100 bg-stone-50 rounded-b-md flex items-baseline justify-between gap-3">
          <div className="text-[10px] text-stone-500 uppercase tracking-wider shrink-0">
            {available.length === 0
              ? `Neutral baseline · ${score} pts`
              : `Total ≈ ${totalContribution} pts`}
          </div>
          {onAdjustWeights && (
            <button
              type="button"
              data-testid="personal-fit-adjust-weights"
              onClick={() => {
                track(EVENTS.PERSONAL_FIT_ADJUST, { score });
                setOpen(false);
                onAdjustWeights();
              }}
              className="text-[11px] font-underground-sc tracking-wider text-[#4C1D95] hover:underline underline-offset-2"
            >
              Adjust these weights →
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
